// src/utils/crypto.js
const enc = new TextEncoder();
const subtle = window.crypto.subtle;

export const PBKDF2_ITERATIONS = 600000;
export const KEYRING_VERSION = 1;

const rand = (n) => window.crypto.getRandomValues(new Uint8Array(n));

export const toB64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};
export const fromB64 = (str) =>
  Uint8Array.from(atob(str), (c) => c.charCodeAt(0));

export const isPassphraseStrongEnough = (p) =>
  typeof p === "string" && p.length >= 12;

// ---------- recovery key format ----------
const bytesToRecovery = (bytes) => {
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return hex.match(/.{8}/g).join("-"); // 8 groups of 8 hex
};
const recoveryToBytes = (str) => {
  const hex = String(str)
    .toLowerCase()
    .replace(/[^0-9a-f]/g, "");
  if (hex.length !== 64) throw new Error("INVALID_RECOVERY_KEY");
  return Uint8Array.from(hex.match(/.{2}/g), (h) => parseInt(h, 16));
};

// ---------- KEK derivation ----------
const kekFromPassphrase = async (passphrase, salt, iterations) => {
  const base = await subtle.importKey(
    "raw",
    enc.encode(passphrase.normalize("NFKC")),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["wrapKey", "unwrapKey"],
  );
};

const kekFromRecovery = (bytes) =>
  subtle.importKey("raw", bytes, "AES-GCM", false, ["wrapKey", "unwrapKey"]);

// ---------- DEK wrap / unwrap ----------
const wrapDEK = async (dek, kek) => {
  const iv = rand(12);
  const data = await subtle.wrapKey("raw", dek, kek, { name: "AES-GCM", iv });
  return { iv: toB64(iv), data: toB64(data) };
};

const unwrapDEK = (wrapped, kek, extractable = false) =>
  subtle.unwrapKey(
    "raw",
    fromB64(wrapped.data),
    kek,
    { name: "AES-GCM", iv: fromB64(wrapped.iv) },
    { name: "AES-GCM", length: 256 },
    extractable,
    ["encrypt", "decrypt"],
  );

// ---------- keyring lifecycle ----------

/** First-time setup. Returns keyring (store in Firestore), session dek, recoveryKey (SHOW ONCE). */
export const createKeyring = async (passphrase) => {
  if (!isPassphraseStrongEnough(passphrase)) throw new Error("WEAK_PASSPHRASE");

  const dek = await subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);

  const salt = rand(16);
  const kek = await kekFromPassphrase(passphrase, salt, PBKDF2_ITERATIONS);
  const recoveryBytes = rand(32);
  const recKek = await kekFromRecovery(recoveryBytes);

  const wrappedByPass = await wrapDEK(dek, kek);
  const wrappedByRecovery = await wrapDEK(dek, recKek);

  const keyring = {
    version: KEYRING_VERSION,
    kdf: {
      name: "PBKDF2",
      hash: "SHA-256",
      iterations: PBKDF2_ITERATIONS,
      salt: toB64(salt),
    },
    wrappedByPass,
    wrappedByRecovery,
  };

  // session key: non-extractable
  const sessionDek = await unwrapDEK(wrappedByPass, kek, false);
  return {
    keyring,
    dek: sessionDek,
    recoveryKey: bytesToRecovery(recoveryBytes),
  };
};

/** Throws if wrong passphrase (AES-GCM auth fail). */
export const unlockWithPassphrase = async (keyring, passphrase) => {
  const kek = await kekFromPassphrase(
    passphrase,
    fromB64(keyring.kdf.salt),
    keyring.kdf.iterations,
  );
  return unwrapDEK(keyring.wrappedByPass, kek, false);
};

export const unlockWithRecovery = async (keyring, recoveryKey) => {
  const kek = await kekFromRecovery(recoveryToBytes(recoveryKey));
  return unwrapDEK(keyring.wrappedByRecovery, kek, false);
};

/** Change passphrase. Return new keyring. Schedules NOT re-encrypted (DEK same). */
export const changePassphrase = async (
  keyring,
  oldPassphrase,
  newPassphrase,
) => {
  if (!isPassphraseStrongEnough(newPassphrase))
    throw new Error("WEAK_PASSPHRASE");
  const oldKek = await kekFromPassphrase(
    oldPassphrase,
    fromB64(keyring.kdf.salt),
    keyring.kdf.iterations,
  );
  const dek = await unwrapDEK(keyring.wrappedByPass, oldKek, true); // temp extractable

  const salt = rand(16);
  const newKek = await kekFromPassphrase(
    newPassphrase,
    salt,
    PBKDF2_ITERATIONS,
  );
  return {
    ...keyring,
    kdf: {
      name: "PBKDF2",
      hash: "SHA-256",
      iterations: PBKDF2_ITERATIONS,
      salt: toB64(salt),
    },
    wrappedByPass: await wrapDEK(dek, newKek),
  };
};

/** Forgot passphrase, have recovery key → set new passphrase. */
export const resetPassphraseWithRecovery = async (
  keyring,
  recoveryKey,
  newPassphrase,
) => {
  if (!isPassphraseStrongEnough(newPassphrase))
    throw new Error("WEAK_PASSPHRASE");
  const recKek = await kekFromRecovery(recoveryToBytes(recoveryKey));
  const dek = await unwrapDEK(keyring.wrappedByRecovery, recKek, true);

  const salt = rand(16);
  const newKek = await kekFromPassphrase(
    newPassphrase,
    salt,
    PBKDF2_ITERATIONS,
  );
  return {
    ...keyring,
    kdf: {
      name: "PBKDF2",
      hash: "SHA-256",
      iterations: PBKDF2_ITERATIONS,
      salt: toB64(salt),
    },
    wrappedByPass: await wrapDEK(dek, newKek),
  };
};

// ---------- data encrypt / decrypt ----------

/** aad = string binding ciphertext to user+doc. */
export const encryptJSON = async (dek, obj, aad) => {
  const iv = rand(12);
  const ct = await subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: enc.encode(aad) },
    dek,
    enc.encode(JSON.stringify(obj)),
  );
  return { iv: toB64(iv), ciphertext: toB64(ct) };
};

export const decryptJSON = async (dek, { iv, ciphertext }, aad) => {
  const pt = await subtle.decrypt(
    { name: "AES-GCM", iv: fromB64(iv), additionalData: enc.encode(aad) },
    dek,
    fromB64(ciphertext),
  );
  return JSON.parse(new TextDecoder().decode(pt));
};
